import { FetchError, LoginResponse, RegisterRequest, User, apiClient } from "../../lib/client/client"
import { UseMutationOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "../../lib/client"

// Types for authentication
export interface LoginCredentials {
  email: string
  password: string
}

export interface ResetPasswordData {
  password: string
  confirmPassword: string
}

// Custom hook for sign in with email/password
export const useSignInWithEmailPass = (
  options?: UseMutationOptions<string, FetchError, LoginCredentials>
) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (payload) => {
      try {
        return await sdk.auth.login("user", "emailpass", payload)
      } catch (error) {
        // Handle specific authentication errors
        if (error instanceof FetchError) {
          if (error.status === 401) {
            throw new FetchError('Invalid credentials', 401, 'Unauthorized')
          }
        }
        throw error
      }
    },
    onSuccess: async (data, variables, context) => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['current-user'] })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

// Custom hook for sign up with email/password
export const useSignUpWithEmailPass = (
  options?: UseMutationOptions<string, FetchError, RegisterRequest>
) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (payload) => sdk.auth.register("user", "emailpass", payload),
    onSuccess: async (data, variables, context) => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: ['current-user'] })
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

// Custom hook for password reset request
export const useResetPasswordForEmailPass = (
  options?: UseMutationOptions<void, FetchError, { email: string }>
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.auth.resetPassword("user", "emailpass", {
        identifier: payload.email,
      }),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

// Custom hook for logout
export const useLogout = (options?: UseMutationOptions<void, FetchError>) => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: () => sdk.auth.logout(),
    onSuccess: async (data, variables, context) => {
      // Clear all cached data on logout
      queryClient.clear()
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

// Custom hook for updating password with token
export const useUpdateProviderForEmailPass = (
  token: string,
  options?: UseMutationOptions<void, FetchError, ResetPasswordData>
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.auth.updateProvider("user", "emailpass", { 
        password: payload.password,
        token 
      }, token),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

// Custom hook to get current user
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: () => {
      const user = apiClient.getCurrentUser()
      if (!user) {
        throw new Error('No authenticated user')
      }
      return user
    },
    enabled: apiClient.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

// Custom hook to check authentication status
export const useAuth = () => {
  const { data: user, isLoading, error } = useCurrentUser()
  
  return {
    user,
    isLoading,
    isAuthenticated: apiClient.isAuthenticated(),
    hasAdminAccess: apiClient.hasAdminAccess(),
    hasSuperAdminAccess: apiClient.hasSuperAdminAccess(),
    error,
  }
}

// Custom hook for role-based access control
export const useRoleCheck = () => {
  const { user } = useAuth()
  
  return {
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    isUser: user?.role === 'user',
    hasRole: (role: 'admin' | 'user' | 'superadmin') => user?.role === role || user?.role === 'superadmin',
  }
}
